import { useEffect, useState, type ReactNode } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { isWebClient, fetchAuthStatus, authSetup, authLogin } from "../../ipc/http-transport";

/**
 * Puerta de autenticación (Fase M4). Solo actúa cuando la app corre como PWA servida por el servidor;
 * en Electron y en desarrollo deja pasar directo. Primer uso → definir contraseña + configurar 2FA
 * (Google Authenticator). Luego → login con contraseña + código de 6 dígitos.
 */
type Phase = "checking" | "setup" | "login" | "ready";

const shellStyle: React.CSSProperties = { minHeight: "100vh", display: "grid", placeItems: "center", padding: "var(--sp-5)", background: "var(--bg-app, #0b0f14)" };

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const [phase, setPhase] = useState<Phase>(isWebClient() ? "checking" : "ready");

  useEffect(() => {
    if (!isWebClient()) return;
    void fetchAuthStatus().then((s) => setPhase(!s.configured ? "setup" : s.authenticated ? "ready" : "login"));
  }, []);

  if (phase === "ready") return <>{children}</>;
  if (phase === "checking") return <div style={shellStyle}><span className="muted">Cargando…</span></div>;
  return (
    <div style={shellStyle}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {phase === "setup" ? <SetupForm onDone={() => setPhase("login")} /> : <LoginForm onOk={() => setPhase("ready")} />}
      </div>
    </div>
  );
}

function SetupForm({ onDone }: { onDone: () => void }): JSX.Element {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (password.length < 8) { setMsg("La contraseña debe tener al menos 8 caracteres."); return; }
    if (password !== confirm) { setMsg("Las contraseñas no coinciden."); return; }
    setBusy(true); setMsg(null);
    const r = await authSetup(password);
    setBusy(false);
    if (!r.ok) { setMsg(r.detail); return; }
    setSecret(r.totpSecret ?? null);
  };

  return (
    <Card>
      <CardHeader title={<span className="row" style={{ gap: 8 }}><Icon name="shield" size={18} /> Configura tu acceso</span>} subtitle="Primer uso: define una contraseña y activa la verificación en dos pasos." />
      <CardBody>
        {secret ? (
          <div style={{ display: "grid", gap: "var(--sp-3)" }}>
            <div className="strong">1) Agrega esta clave en Google Authenticator</div>
            <div className="faint" style={{ fontSize: 12.5 }}>App → “Ingresar clave de configuración” → cuenta “FABIAN-MELI”, clave:</div>
            <code style={{ fontSize: 15, letterSpacing: 1, padding: "10px 12px", background: "var(--bg-overlay, #111)", borderRadius: 8, wordBreak: "break-all" }}>{secret}</code>
            <div className="faint" style={{ fontSize: 12 }}>Guárdala en un lugar seguro. Luego inicia sesión con tu contraseña y el código de 6 dígitos.</div>
            <Button onClick={onDone}>Continuar al inicio de sesión</Button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "var(--sp-3)" }}>
            {msg ? <div className="callout"><div className="bar" /><div className="body">{msg}</div></div> : null}
            <input className="pd-input" type="password" placeholder="Contraseña (mín. 8)" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Contraseña" />
            <input className="pd-input" type="password" placeholder="Repite la contraseña" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void submit(); }} aria-label="Repite la contraseña" />
            <Button icon="check" disabled={busy} onClick={submit}>{busy ? "Configurando…" : "Crear acceso"}</Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LoginForm({ onOk }: { onOk: () => void }): JSX.Element {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    const r = await authLogin(password, code.trim());
    setBusy(false);
    if (r.ok) onOk(); else setMsg(r.detail);
  };

  return (
    <Card>
      <CardHeader title={<span className="row" style={{ gap: 8 }}><Icon name="shield" size={18} /> Iniciar sesión</span>} subtitle="Ingresa tu contraseña y el código de tu app de autenticación." />
      <CardBody>
        <div style={{ display: "grid", gap: "var(--sp-3)" }}>
          {msg ? <div className="callout"><div className="bar" /><div className="body">{msg}</div></div> : null}
          <input className="pd-input" type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Contraseña" />
          <input className="pd-input" inputMode="numeric" autoComplete="one-time-code" placeholder="Código de 6 dígitos" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => { if (e.key === "Enter") void submit(); }} aria-label="Código de verificación" />
          <Button icon="check" disabled={busy || password.length === 0 || code.length !== 6} onClick={submit}>{busy ? "Entrando…" : "Entrar"}</Button>
        </div>
      </CardBody>
    </Card>
  );
}
